import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const BranchContext = createContext(null);
const STORAGE_KEY = "zed_branch_type";
const VALID = new Set(["full", "drivethru"]);

function readInitial() {
  if (typeof window === "undefined") return null;
  try {
    // 1) URL param wins ( ?branch=drivethru | full )
    const url = new URL(window.location.href);
    const raw = (url.searchParams.get("branch") || "").toLowerCase();
    const normalized =
      raw === "drive-thru" || raw === "drive_thru" ? "drivethru" : raw;
    if (VALID.has(normalized)) {
      localStorage.setItem(STORAGE_KEY, normalized);
      return normalized;
    }
    // 2) localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (VALID.has(stored)) return stored;
  } catch (_) {}
  return null;
}

export function BranchProvider({ children }) {
  const [branchType, setBranchTypeState] = useState(readInitial);

  const setBranchType = useCallback((v) => {
    if (!VALID.has(v)) return;
    localStorage.setItem(STORAGE_KEY, v);
    setBranchTypeState(v);
  }, []);

  const clearBranchType = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setBranchTypeState(null);
  }, []);

  // Keep in sync when URL updates client-side
  useEffect(() => {
    const onPop = () => {
      const v = readInitial();
      if (v && v !== branchType) setBranchTypeState(v);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [branchType]);

  const value = useMemo(
    () => ({ branchType, setBranchType, clearBranchType, isDriveThru: branchType === "drivethru" }),
    [branchType, setBranchType, clearBranchType]
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export const useBranch = () => useContext(BranchContext);
